import { useEffect, useState } from "react";
import { api } from "./lib/api";
import { Rate, Count, Vintage, type Freshness } from "./components/provenance";

// Portal Home — staff landing. Base-vault coverage, account counts, import health, quick links.
const BORDER = "1px solid rgba(128,128,128,.35)";
type Coverage = {
  states: { state: string; voters: number; households: number }[];
  totals: { voters: number; households: number };
  // null = the rollup has never run. NOT a row of zeros: "0 addresses, 0% findable" is a
  // measurement we have not taken, stated as a fact.
  addresses: {
    counties: number; unmeasured: number; total: number;
    // MEASURED (footprint_check, via footprint_run) and LABELLED (household_confidence_tier).
    // Never the same word on this screen, because they are not the same claim.
    checked: number; on_a_building: number;
    // NEITHER CONFIRMED NOR UNCONFIRMED (0237): the addr_key names more than one street, so
    // the verdict we hold for it was measured at a different address. null until a rollup
    // runs after 0237, and zero only once the collision audit has actually written.
    key_unattributable: number | null;
    on_the_building: number; conflicted: number;
    // The share of `conflicted` in counties whose own parcel data makes the flag believable. The
    // total on its own points at a pile a fifth of which is the county's parcel file (0212).
    conflicted_credible: number;
  } | null;
  // THE LEAD NUMBER'S OWN BLOCK, and null on the same rule as `addresses`: a portal deployed
  // ahead of a land-use-aware rollup must render an em dash, not "0.0% of residential pins are
  // on a roof" -- the most alarming possible way to say nobody has run the job yet.
  residential: {
    counties: number; sf_addresses: number; sf_on_the_building: number;
    sf_has_building: number; land_use_unknown: number;
    // MEASURED against building footprints, and the denominator it is allowed to have. The
    // labelled field above reads about eight points higher and is kept only for comparison.
    sf_checked: number; sf_on_a_building: number; sf_near_building: number | null;
  } | null;
  // WHEN every address-vault figure above was taken. Optional because a portal build can reach an
  // API in front of a database that has not run 0231 yet; Vintage renders nothing for a missing
  // block rather than inventing a reassuring date.
  freshness?: Freshness | null;
  last_import: string | null;
  import_health: Record<string, number>;
};
const n = (x: number) => x.toLocaleString();
const share = (part: number, whole: number) => (whole > 0 ? `${((100 * part) / whole).toFixed(1)}%` : "—");

function Stat({ label, value, sub, lead }: { label: string; value: string; sub?: string; lead?: boolean }) {
  return (
    <div className="card" style={{ padding: 14, minWidth: 150, flex: lead ? 1.6 : 1,
                                   borderColor: lead ? "var(--accent,#0ea5a4)" : undefined }}>
      <div className="muted" style={{ fontSize: 12 }}>{label}</div>
      <div style={{ fontWeight: 800, fontSize: lead ? 30 : 24, lineHeight: 1.1, marginTop: 2,
                    color: lead ? "var(--accent,#0ea5a4)" : undefined }}>{value}</div>
      {sub && <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

export function Home({ onGo, accounts, accountsOk, openFindings }: {
  onGo: (tab: string) => void;
  // HANDED DOWN, NOT FETCHED AGAIN. Portal loads the account list and the findings summary on
  // mount, and Home used to request both a SECOND time, in parallel with it. Resource Timing on
  // 2026-08-29 shows the duplicates plainly: two /portal/accounts and two /portal/findings-summary
  // at the same startTime on one Home load. Owning the list in one place also removes the chance
  // of the two views disagreeing about which accounts exist.
  accounts: { status: string }[];
  // `active` is derived from a list that is empty both when there are no accounts and when the
  // request failed — so a failed load rendered a confident "Active accounts 0 · 0 total". A hard
  // zero stated as fact. `—` is the honest reading of "we do not know", and that distinction has
  // to survive the move up to Portal, so the flag travels with the list.
  accountsOk: boolean;
  // null = we do not know (the request failed); never conflated with a real 0.
  openFindings: number | null;
}) {
  const [cov, setCov] = useState<Coverage | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try { setCov(await api<Coverage>("/portal/vault-coverage")); }
      catch (e: any) { setErr(String(e.message || e)); }
      finally { setLoading(false); }
    })();
  }, []);

  const active = accounts.filter((a) => a.status === "active").length;
  const addr = cov?.addresses ?? null;
  const res = cov?.residential ?? null;
  const done = cov?.import_health?.done || 0;
  const failed = cov?.import_health?.error || 0;
  const last = cov?.last_import ? new Date(cov.last_import).toLocaleDateString() : "—";

  return (
    <div className="wrap">
      <div className="row" style={{ marginBottom: 14 }}>
        <h2 style={{ margin: 0 }}>Home</h2>
        <span className="muted">Platform overview</span>
      </div>
      {err && <div className="err" style={{ marginBottom: 10 }}>{err}</div>}
      {loading ? <div className="muted">Loading…</div> : (
        <>
          <div className="row" style={{ gap: 12, marginBottom: 14, flexWrap: "wrap", alignItems: "stretch" }}>
            {/* THE RESIDENTIAL RATE LEADS, AND THE OLD HEADLINE WAS A DIFFERENT QUESTION.
                "Doors the crew can find" divided rooftop pins by EVERY address a county
                publishes -- pipeline easements, cell towers, county sheds, vacant lots -- and read
                89.8%. Over the addresses an appraisal district actually calls single-family it is
                97.1%. Neither number is wrong; the first one is not a canvassing figure and was
                being read as one. The all-address share is kept below, labelled as what it is.

                THE DENOMINATOR TRAVELS WITH THE PERCENTAGE, always. A rate quoted without saying
                how many counties it speaks for is a share of an unstated denominator, which is
                the failure this whole rework exists to remove. */}
            {/* TWO MEASURED RATES, AND THE RESIDENTIAL ONE LEADS. Both divide by pins actually
                TESTED against Overture footprints, never by every address -- dividing by the file
                counts a scan that has not run as a failure, which 0197, 0207 and 0210 each had to
                remove from a different screen.

                THEY ARE NOT THE SAME QUESTION, WHICH IS WHY BOTH ARE HERE. The all-uses rate
                includes easements, cell towers and vacant lots. The residential rate is the share
                of single-family pins on a building, and that is the number a walk list is built
                from -- so it is the one that leads.

                NEITHER IS THE ROOFTOP LABEL. household_confidence_tier is the county's own
                geocode_quality for any address we did not place, inherited from TxGIO, which
                aggregates 48 911 authorities and publishes no placement-method field. Bexar reads
                99.8 per cent labelled against 90.8 measured. 0215 established that once and 0222
                lost it again; the label now appears only beside the measurement it is read against.

                THROUGH THE SAME PRIMITIVE AS THE COVERAGE SCREEN. Rate() takes the denominator and
                the provenance as required arguments, so neither can be left off here and then
                differ from there -- which is how the same measurement came to be quoted three
                different ways on three screens. */}
            <div className="card" style={{ padding: 14, minWidth: 230, flex: 1.8,
                                           borderColor: "var(--accent,#0ea5a4)" }}>
              <div className="muted" style={{ fontSize: 12, marginBottom: 4 }}>Residential pins on a building</div>
              <Rate part={res?.sf_on_a_building} whole={res?.sf_checked} kind="measured"
                    of={res ? `single-family pins tested against Overture footprints, in ${res.counties} counties`
                            : "single-family pins tested against Overture footprints"}
                    unmeasured="No single-family pin has been footprint-checked yet. This is not a zero." />
            </div>
            {/* ON THE BUILDING *OR* WITHIN A HUNDRED FEET OF ONE (0237).
                `on a building` grades the county's placement CONVENTION as much as the pin: a rural
                911 entity puts the pin at the driveway so a responder knows which track to turn up.
                Measured -- the pins NOT inside a building sit a median 36.2 m from one in Bexar and
                38.4 m in Robertson, the same distance, while Bexar passes at 80.9 per cent and
                Robertson at 0.5. Beside the strict number rather than replacing it, because the two
                answer different questions and hiding either is how a label gets headlined.
                null, not zero, until a rollup runs after 0237 -- Rate() renders `unmeasured`. */}
            <div className="card" style={{ padding: 14, minWidth: 230, flex: 1.6 }}>
              <div className="muted" style={{ fontSize: 12, marginBottom: 4 }}>Residential pins at the door</div>
              <Rate part={res == null || res.sf_near_building == null ? undefined
                                : res.sf_on_a_building + res.sf_near_building}
                    whole={res?.sf_checked} kind="measured"
                    of="single-family pins on a building, or within 100 feet of one"
                    unmeasured="No county has been rolled up since the within-100-feet measure was added. This is not a zero." />
            </div>
            <div className="card" style={{ padding: 14, minWidth: 210, flex: 1.4 }}>
              <div className="muted" style={{ fontSize: 12, marginBottom: 4 }}>Pins on a building, all uses</div>
              <Rate part={addr?.on_a_building} whole={addr?.checked} kind="measured"
                    of="pins tested against Overture footprints, all land uses"
                    unmeasured="No pin has been footprint-checked yet. This is not a zero." />
            </div>
            {/* THE ADDRESSES THAT HOLD SOMEONE ELSE'S ANSWER (0237). addr_key drops the trailing
                street type on purpose -- it was built to MATCH a voter file against a parcel file
                (+63,026 matches) -- so Bexar's 100 DALEWOOD PLACE and 100 DALEWOOD DR, 13 km apart,
                are one key and one footprint_check row. 41,821 keys over 89,518 rows, 0.659% of the
                state. address_point keeps each address's OWN coordinate, so no canvasser is walked
                to the wrong door; only the verdict is borrowed. It is shown because a borrowed
                verdict counted as a verdict is the whole defect -- these are excluded from both
                confirmed and unconfirmed rather than quietly padding either. */}
            <div className="card" style={{ padding: 14, minWidth: 190, flex: 1.1 }}>
              <div className="muted" style={{ fontSize: 12, marginBottom: 4 }}>Addresses with no verdict of their own</div>
              <Count value={addr?.key_unattributable} kind="measured"
                     of="share an address key with a different street, so their footprint answer was measured elsewhere"
                     unmeasured="The key-collision audit has not been run against this database yet. This is not a zero." />
            </div>
            <div className="card" style={{ padding: 14, minWidth: 150, flex: 1 }}>
              <div className="muted" style={{ fontSize: 12, marginBottom: 4 }}>Single-family doors</div>
              <Count value={res?.sf_has_building} kind="measured"
                     of={res ? `on a lot with a building, of ${n(res.sf_addresses)} single-family addresses in ${res.counties} counties` : ""}
                     unmeasured="No county has been rolled up with land use yet." />
            </div>
            {/* THE DATE UNDER THE THREE VAULT FIGURES, and under those three only. The account,
                import and voter-file numbers beside them are live reads, not snapshot reads, and a
                banner spanning both would date numbers that carry their own freshness. */}
            <div style={{ flexBasis: "100%" }}><Vintage f={cov?.freshness} /></div>
            {/* ADDRESSES FIRST, AND THEN VOTERS. Home led with 312,538 voters, which is the
                licensed voter file; the address vault underneath it holds 12,310,602 addresses and
                is what every walk list is actually built from. Reading them in the other order left
                staff with a mental model of the platform two orders of magnitude too small. */}
            <Stat label="Addresses in the vault" value={addr ? n(addr.total) : "—"}
                  sub={addr ? `${addr.counties} counties · ${share(addr.on_the_building, addr.total)} on a rooftop pin, all uses${addr.unmeasured ? ` · ${addr.unmeasured} not yet measured` : ""}` : undefined} />
            <Stat label="Voters in vault" value={cov ? n(cov.totals.voters) : "—"} sub={cov ? `${cov.states.length} state${cov.states.length === 1 ? "" : "s"}` : undefined} />
            <Stat label="Households" value={cov ? n(cov.totals.households) : "—"} />
            <Stat label="Active accounts" value={accountsOk ? n(active) : "—"} sub={accountsOk ? `${accounts.length} total` : undefined} />
            <Stat label="Imports (30d)" value={n(done)} sub={failed ? `${failed} failed` : "all clean"} />
            <Stat label="Last import" value={last} />
            {openFindings != null && openFindings > 0 && <Stat label="Data-quality findings" value={n(openFindings)} sub="open across accounts" />}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, alignItems: "start" }}>
            <div className="card" style={{ padding: 0 }}>
              <div style={{ padding: "8px 12px", borderBottom: BORDER, fontWeight: 700 }}>Voter file by state</div>
              <table>
                <thead><tr><th>State</th><th style={{ textAlign: "right" }}>Voters</th><th style={{ textAlign: "right" }}>Households</th></tr></thead>
                <tbody>
                  {(!cov || cov.states.length === 0) && (
                    <tr><td colSpan={3} className="muted" style={!cov && err ? { color: "var(--warn,#eab308)" } : undefined}>
                      {!cov && err ? "Couldn't load the voter file — this is not an empty vault." : "No voter data loaded yet."}
                    </td></tr>
                  )}
                  {cov?.states.map((s) => (
                    <tr key={s.state || "—"}>
                      <td style={{ fontWeight: 700 }}>{s.state || "—"}</td>
                      <td style={{ textAlign: "right" }}>{n(s.voters)}</td>
                      <td style={{ textAlign: "right" }} className="muted">{n(s.households)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="card" style={{ padding: 14 }}>
              {addr && addr.conflicted > 0 && (
                // The largest pile of work in the platform, and until now it appeared on no screen
                // at all -- not here, and not on the coverage scorecard either.
                <div style={{ marginBottom: 12, paddingBottom: 12, borderBottom: BORDER }}>
                  <div className="muted" style={{ fontSize: 12 }}>Pins in conflict, worth a look</div>
                  <div style={{ fontWeight: 800, fontSize: 22, lineHeight: 1.2, color: "var(--warn,#eab308)" }}>
                    {n(addr.conflicted_credible)}
                  </div>
                  <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>
                    Addresses whose pin did not land on the parcel bearing that address and which
                    nothing has corrected yet — placing a pin removes one from this count — in counties
                    whose parcel data makes the flag worth believing. {n(addr.conflicted)} are open in
                    total; the rest sit in counties whose parcel file is too poor for the flag to mean much.
                  </div>
                  {/* IT USED TO SAY "Work them in Geocodes → Triage leftovers", AND THAT QUEUE
                      CANNOT SHOW THESE. It runs private.geocode_suspects() over public.household --
                      the licensed voter file -- with five unrelated reasons, and refuses to run
                      without a district picked first. These are address_vault.address_point rows
                      with geocode_quality 'suspect', which is what address_vault.triage_queue()
                      selects, statewide, on the Vault tab. Anyone who followed that sentence worked
                      a queue with no relationship to the number that sent them. */}
                  <button className="btn sm" style={{ marginTop: 8 }}
                          onClick={() => onGo("vault")}>Work them in Vault → Address review →</button>
                </div>
              )}
              <div style={{ fontWeight: 700, marginBottom: 8 }}>Jump in</div>
              <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
                <button className="btn" onClick={() => onGo("import")}>Import data →</button>
                <button className="btn" onClick={() => onGo("geocode")}>Geocodes →</button>
                <button className="btn" onClick={() => onGo("accounts")}>Client accounts →</button>
                <button className="btn" onClick={() => onGo("team")}>Team →</button>
                <button className="btn" onClick={() => onGo("audit")}>Activity log →</button>
              </div>
              <div className="muted" style={{ fontSize: 12, marginTop: 12 }}>
                <b>Measured, not labelled.</b> Both headline rates divide pins found inside an
                Overture building footprint by the pins actually tested — never by every address,
                which would count an unrun scan as a failure. The leading rate is the
                <i>single-family</i> slice: of the addresses a county's appraisal district files as
                residential, the share whose pin was tested and found inside a building. The
                all-uses rate beside it includes easements, cell towers and vacant lots, so it
                answers a different question rather than a rounder version of the same one. Neither
                is the rooftop <i>label</i>, which is inherited from 911 authorities that publish no
                placement method and reads several points higher{res && res.sf_checked > 0 && <> ({share(res.sf_on_the_building, res.sf_checked)} against
                the measured {share(res.sf_on_a_building, res.sf_checked)} on single-family)</>}.
                Counties publishing no land use are left out of the residential rate rather than
                scored zero. Every subtraction between 12.3M addresses and the door count — and the
                per-county breakdown — is on the <b>Vault</b> tab.
              </div>
              <div className="muted" style={{ fontSize: 12, marginTop: 12 }}>
                Two things share the name "vault". The <b>voter file</b> is the licensed base data every
                client account draws from — load it under Import. The <b>address vault</b> is every address
                in the state with a pin on it, which is what walk lists are cut from — its per-county
                scorecard lives under Geocodes → Vault coverage.
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
